import { useEffect, useRef, useState } from 'react';

/**
 * 374 (US6): sticky links to the booking page's sections. The page has a dozen cards in one column, and which ones
 * render depends on the booking and the reader's permissions, so the links are read from the page itself: each card
 * names its group with `data-jump`, and the first card of each group is the target. A group with no card on screen
 * has no link, and the order is the page's own.
 */
export function BookingJumpLinks({
  containerRef,
}: { containerRef: React.RefObject<HTMLElement | null> }) {
  const [groups, setGroups] = useState<string[]>([]);
  const targets = useRef(new Map<string, HTMLElement>());

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const read = () => {
      const found = new Map<string, HTMLElement>();
      for (const el of root.querySelectorAll<HTMLElement>('[data-jump]')) {
        const name = el.dataset.jump;
        if (name && !found.has(name)) found.set(name, el);
      }
      targets.current = found;
      const names = [...found.keys()];
      setGroups((prev) => (prev.join('|') === names.join('|') ? prev : names));
    };
    read();
    // Cards appear as their data arrives (the inspections, the payments), so re-read on change.
    const observer = new MutationObserver(read);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [containerRef]);

  if (groups.length < 2) return null;
  return (
    <nav
      aria-label="Booking sections"
      data-testid="booking-jump-links"
      className="sticky top-0 z-10 -mx-1 flex flex-wrap gap-1 bg-surface px-1 py-1.5"
    >
      {groups.map((name) => (
        <button
          key={name}
          type="button"
          onClick={() =>
            targets.current.get(name)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          className="rounded-full border border-hairline bg-canvas px-3 py-1 text-xs font-semibold text-steel hover:border-primary hover:text-primary"
        >
          {name}
        </button>
      ))}
    </nav>
  );
}
